import { useState } from 'react';
import { api } from '../lib/api.js';
import { useEmailVerification, isEmailUnverifiedError } from '../hooks/useEmailVerification.js';
import { ConfirmEmailNotice } from './ConfirmEmailNotice.jsx';
import { Button, Alert } from './ui.jsx';

/**
 * The brand to act on, when it may not be the dashboard's current one: an
 * account running several brands can open any of their pages and publish it
 * from there.
 */
const brandHeaders = (profile, kind) =>
  kind === 'brand' && profile?.id ? { headers: { 'X-Affily-Brand': profile.id } } : undefined;

/**
 * The other half of the publish gate.
 *
 * An agent can build someone a whole profile, but nothing it builds is public
 * until this button is pressed. Without this screen the gate would have an
 * entrance and no exit — a profile could go in and never come out.
 *
 * The copy is deliberately specific about what publishing means, because the
 * person reading it may not have written a word of what they are about to put
 * their name on.
 */
export function DraftBanner({ profile, kind, onPublished }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { verified, refresh } = useEmailVerification();

  if (!profile || profile.isPublished) return null;

  const builtByAgent = profile.createdVia === 'api';
  const endpoint = kind === 'brand' ? '/brands/me' : '/creators/me';

  async function publish() {
    setBusy(true);
    setError('');
    try {
      await api.patch(endpoint, { isPublished: true }, brandHeaders(profile, kind));
      onPublished?.();
    } catch (err) {
      setError(err.message);
      // The server is the authority: re-read the account so the button and
      // notice reflect whether the email really is unconfirmed.
      if (isEmailUnverifiedError(err)) refresh().catch(() => {});
      setBusy(false);
    }
  }

  return (
    <section className="mb-6 rounded-lg border border-caution/30 bg-caution-soft p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <h2 className="font-semibold text-ink">
            {builtByAgent
              ? 'An agent built this profile for you — nothing is public yet'
              : 'This profile is a draft'}
          </h2>

          <p className="mt-1.5 text-sm text-ink-muted">
            {builtByAgent ? (
              <>
                It is not in the directory, not in search, and its page returns a 404 to anyone who
                tries it. Read it over before you publish — once it is live, these numbers are
                attributed to you.
              </>
            ) : (
              <>
                It is not in the directory or in search yet. Publish when you are ready.
              </>
            )}
          </p>

          {builtByAgent && kind !== 'brand' && (
            <p className="mt-2 text-xs text-ink-faint">
              Worth checking the audience figures in particular. Affily labels them as self-reported
              wherever they appear, but they will be published as your claim.
            </p>
          )}
        </div>

        <Button onClick={publish} disabled={busy || !verified} size="lg">
          {busy ? 'Publishing…' : 'Review looks right — publish'}
        </Button>
      </div>

      <ConfirmEmailNotice className="mt-3" />

      {error && (
        <Alert tone="negative" className="mt-3">
          {error}
        </Alert>
      )}
    </section>
  );
}

/** Inverse control, for a published profile that someone wants to take down. */
export function UnpublishControl({ profile, kind, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!profile?.isPublished) return null;

  const endpoint = kind === 'brand' ? '/brands/me' : '/creators/me';

  return (
    <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-rule pt-5">
      <span className="text-sm text-ink-muted">
        This profile is live in the directory.
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await api.patch(endpoint, { isPublished: false }, brandHeaders(profile, kind));
            onChanged?.();
          } finally {
            setBusy(false);
          }
        }}
        className="text-sm text-ink-muted underline hover:text-ink"
      >
        {busy ? 'Unpublishing…' : 'Unpublish'}
      </button>
    </div>
  );
}
